import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { dateTime, moneyIn, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * Couriers: shipping labels bought from the courier service the business connected, and each
 * parcel followed until it is delivered. Prices are the courier's own.
 */

type Track = 'unknown' | 'pre_transit' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'available_for_pickup' | 'return_to_sender' | 'failure' | 'cancelled' | 'error';
const TRACK: Record<Track, { tone: Tone; icon: IconName }> = {
  unknown: { tone: 'neutral', icon: 'clock' },
  pre_transit: { tone: 'info', icon: 'box' },
  in_transit: { tone: 'info', icon: 'truck' },
  out_for_delivery: { tone: 'info', icon: 'truck' },
  delivered: { tone: 'ok', icon: 'checkCircle' },
  available_for_pickup: { tone: 'warn', icon: 'store' },
  return_to_sender: { tone: 'danger', icon: 'undo' },
  failure: { tone: 'danger', icon: 'alert' },
  cancelled: { tone: 'neutral', icon: 'x' },
  error: { tone: 'danger', icon: 'alert' },
};

export interface ShipAddress {
  name: string;
  company?: string | null;
  street1: string;
  street2?: string | null;
  city: string;
  state?: string | null;
  zip: string;
  country: string;
  phone?: string | null;
  email?: string | null;
}
interface Rate {
  ref: string;
  carrier: string;
  service: string;
  amount: number;
  currency: string;
  days: number | null;
}
interface TrackEvent {
  at: string | null;
  status: Track;
  message: string;
}
export interface ShipmentView {
  id: string;
  module: string | null;
  record: string | null;
  label: string;
  state: 'quoted' | 'bought' | 'dropped';
  live: boolean;
  to_address: ShipAddress;
  parcel: { weight_g: number; length_mm: number | null; width_mm: number | null; height_mm: number | null };
  customs: { contents_type: ContentsType; currency: string; items: { description: string; quantity: number; value: number }[] } | null;
  rates: Rate[];
  carrier: string | null;
  service: string | null;
  amount: number | null;
  currency: string | null;
  days: number | null;
  tracking_code: string | null;
  label_url: string | null;
  public_url: string | null;
  track_status: Track | null;
  track_detail: string | null;
  est_delivery: string | null;
  events: TrackEvent[];
  tracked_at: string | null;
  delivered_at: string | null;
  refund_state: 'submitted' | 'refunded' | 'rejected' | null;
  error: string | null;
  bought_at: string | null;
  messages?: string[];
}
interface Defaults {
  units: 'metric' | 'imperial';
  weight_g: number;
  length_mm: number | null;
  width_mm: number | null;
  height_mm: number | null;
  country: string | null;
  currency: string;
}
export interface ForRecord {
  ready: { courier: string; test: boolean } | null;
  problems: string[];
  defaults: Defaults;
  shipments: ShipmentView[];
}

const trackOf = (s: ShipmentView): Track => s.track_status ?? 'unknown';
export function TrackBadge({ s }: { s: ShipmentView }) {
  if (s.refund_state) return <StatusBadge tone={s.refund_state === 'rejected' ? 'danger' : 'neutral'} icon="x" label={t(`sh.refund_${s.refund_state}` as MessageKey)} />;
  const k = trackOf(s);
  return <StatusBadge tone={TRACK[k].tone} icon={TRACK[k].icon} label={t(`sh.t_${k}` as MessageKey)} />;
}

// ---- units: grams and millimetres inside, kilograms/centimetres or pounds/inches on screen ----

const G_PER_LB = 453.59237;
const showWeight = (g: number, units: Defaults['units']) => (units === 'imperial' ? String(Math.round((g / G_PER_LB) * 100) / 100) : String(Math.round(g / 10) / 100));
const showSize = (mm: number | null, units: Defaults['units']) => (mm == null ? '' : units === 'imperial' ? String(Math.round((mm / 25.4) * 10) / 10) : String(Math.round(mm / 10)));
const readNumber = (s: string) => {
  const n = Number(s.replace(',', '.'));
  return s.trim() && Number.isFinite(n) && n > 0 ? n : null;
};
const toGrams = (s: string, units: Defaults['units']) => {
  const n = readNumber(s);
  return n == null ? null : Math.round(units === 'imperial' ? n * G_PER_LB : n * 1000);
};
const toMm = (s: string, units: Defaults['units']) => {
  const n = readNumber(s);
  return n == null ? null : Math.round(units === 'imperial' ? n * 25.4 : n * 10);
};
export const weightText = (g: number, units: Defaults['units']) => `${showWeight(g, units)} ${units === 'imperial' ? 'lb' : 'kg'}`;

// ---- the page ----------------------------------------------------------------------------------

interface Overview {
  ready: { account: string; courier: string; test: boolean } | null;
  problems: string[];
  kpis: { sent_30d: number; moving: number; delivered_30d: number; problems: number; spent_30d: { currency: string; amount: number }[] };
  shipments: ShipmentView[];
}
const FILTERS = ['all', 'moving', 'delivered', 'problems', 'cancelled'] as const;
type Filter = (typeof FILTERS)[number];

export function Shipping() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const manage = can('shipping.manage');
  const tab = manage && params.get('tab') === 'settings' ? 'settings' : 'parcels';
  const filter = (FILTERS.includes(params.get('f') as Filter) ? params.get('f') : 'all') as Filter;
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setQuery(q.trim()), 250);
    return () => window.clearTimeout(id);
  }, [q]);
  const { data: o, reload } = useAction<Overview>('shipping.overview', { filter, ...(query ? { q: query } : {}) });
  useEffect(() => subscribe(['shipping'], () => void reload()), [reload]);
  const [open, setOpen] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const setTab = (k: 'parcels' | 'settings') => setParams(k === 'parcels' ? {} : { tab: k }, { replace: true });
  const spent = o?.kpis.spent_30d ?? [];
  return (
    <Shell>
      <div className="page reports pm-page">
        <div className="page-head">
          <div>
            <h1>{t('sh.title')}</h1>
            <p className="muted">{!o ? ' ' : o.ready ? t('sh.lead_ready', { account: o.ready.account }) : t('sh.lead_not_ready')}</p>
          </div>
          {o?.ready && can('shipping.ship') && tab === 'parcels' ? (
            <Button variant="primary" icon="plus" onClick={() => setMaking(true)}>
              {t('sh.new')}
            </Button>
          ) : null}
        </div>
        {o?.ready?.test ? (
          <p className="callout callout-warn pm-test" role="note">
            <Icon name="alert" size={16} />
            <span>{o.ready.courier === 'simulator' ? t('sh.test_simulator') : t('sh.test_key')}</span>
          </p>
        ) : null}
        {o && !o.ready ? (
          <div className="callout callout-info pm-setup">
            <Icon name="truck" size={20} />
            <div>
              <p>
                <strong>{t('sh.setup_title')}</strong>
              </p>
              <p className="muted">{o.problems.includes('account') ? t('sh.setup_body') : o.problems.includes('sender') ? t('sh.setup_sender') : t('sh.setup_key')}</p>
              {manage ? (
                <Button size="sm" icon="settings" onClick={() => setTab('settings')}>
                  {t('sh.setup_go')}
                </Button>
              ) : (
                <p className="small muted">{t('sh.setup_ask')}</p>
              )}
            </div>
          </div>
        ) : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('sh.at_a_glance')}>
            <Kpi label={t('sh.k_sent')} value={String(o.kpis.sent_30d)} hint={t('sh.k_30_days')} />
            <Kpi label={t('sh.k_moving')} value={String(o.kpis.moving)} hint={t('sh.k_moving_hint')} />
            <Kpi label={t('sh.k_delivered')} value={String(o.kpis.delivered_30d)} hint={t('sh.k_30_days')} />
            <Kpi
              label={t('sh.k_spent')}
              value={spent[0] ? moneyIn(spent[0].amount, spent[0].currency) : '—'}
              hint={
                o.kpis.problems
                  ? tn('sh.k_problems', o.kpis.problems)
                  : spent.length > 1
                    ? spent
                        .slice(1)
                        .map((x) => moneyIn(x.amount, x.currency))
                        .join(' · ')
                    : t('sh.k_30_days')
              }
              warn={o.kpis.problems > 0}
            />
          </section>
        )}
        {manage ? (
          <div className="segmented tabs" role="tablist" aria-label={t('sh.sections')}>
            {(['parcels', 'settings'] as const).map((k) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                <Icon name={k === 'parcels' ? 'box' : 'settings'} size={16} />
                {t(`sh.tab_${k}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : null}
        {tab === 'settings' ? (
          <SettingsTab onChange={reload} />
        ) : (
          <section className="panel pm-list">
            <div className="pm-tools">
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" value={q} placeholder={t('sh.search')} aria-label={t('sh.search')} onChange={(e) => setQ(e.target.value)} />
              </label>
              <div className="hd-filters" role="group" aria-label={t('sh.show')}>
                {FILTERS.map((f) => (
                  <button key={f} className={`hd-chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setParams(f === 'all' ? {} : { f }, { replace: true })}>
                    {t(`sh.f_${f}` as MessageKey)}
                  </button>
                ))}
              </div>
            </div>
            {!o ? (
              <Skeleton h={240} />
            ) : !o.shipments.length && filter === 'all' && !query ? (
              <EmptyState
                icon="truck"
                title={t('sh.empty_title')}
                body={t('sh.empty_body')}
                action={
                  o.ready && can('shipping.ship') ? (
                    <Button icon="plus" onClick={() => setMaking(true)}>
                      {t('sh.new')}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <Table
                head={[t('sh.col_to'), t('sh.col_carrier'), t('sh.col_when'), t('sh.col_state'), t('sh.col_cost')]}
                left={[1, 2, 3]}
                phoneHide={[1, 2]}
                empty={t('sh.none_here')}
                rows={o.shipments.map((s) => ({
                  key: s.id,
                  onClick: () => setOpen(s.id),
                  cells: [
                    <span className="pm-who" key="who">
                      <strong>{s.to_address.name}</strong>
                      <span className="muted small">{[s.label, s.to_address.city].filter(Boolean).join(' · ')}</span>
                    </span>,
                    <span className="pm-who" key="c">
                      <span>{[s.carrier, s.service].filter(Boolean).join(' · ')}</span>
                      {s.tracking_code ? <span className="muted small sh-code">{s.tracking_code}</span> : null}
                    </span>,
                    dateTime(s.bought_at ?? ''),
                    <span className="pm-state" key="st">
                      <TrackBadge s={s} />
                      {s.est_delivery && !s.delivered_at && !s.refund_state ? <span className="muted small">{t('sh.expected', { date: dateTime(s.est_delivery) })}</span> : null}
                    </span>,
                    s.amount != null ? moneyIn(s.amount, s.currency) : '—',
                  ],
                }))}
              />
            )}
          </section>
        )}
        {open ? <ShipmentDialog id={open} onClose={() => setOpen(null)} /> : null}
        {making ? <CourierDialog label="" onClose={() => setMaking(false)} onBought={() => void reload()} /> : null}
      </div>
    </Shell>
  );
}

// ---- one parcel --------------------------------------------------------------------------------

export function ShipmentDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: s, reload } = useAction<ShipmentView>('shipping.get', { id });
  const [cancel, setCancel] = useState(false);
  const [checking, setChecking] = useState(false);
  const { fail } = useUi();
  if (!s) {
    return (
      <Dialog title={t('sh.parcel')} onClose={onClose} size="md">
        <Skeleton h={240} />
      </Dialog>
    );
  }
  const a = s.to_address;
  const check = async () => {
    setChecking(true);
    try {
      await action('shipping.refresh', { id });
      await reload();
    } catch (err) {
      fail(err);
    }
    setChecking(false);
  };
  const cancellable = can('shipping.ship') && !s.refund_state && (!s.track_status || s.track_status === 'pre_transit' || s.track_status === 'unknown');
  return (
    <Dialog
      title={s.label || a.name}
      onClose={onClose}
      size="md"
      footer={
        <>
          {cancellable ? (
            <Button variant="ghost" icon="x" onClick={() => setCancel(true)}>
              {t('sh.cancel_label')}
            </Button>
          ) : (
            <span />
          )}
          <span className="pm-actions">
            {!s.refund_state && s.track_status !== 'delivered' ? (
              <Button icon="repeat" busy={checking} onClick={() => void check()}>
                {t('sh.check_now')}
              </Button>
            ) : null}
            {s.label_url ? (
              <a className="btn btn-primary btn-md" href={s.label_url} target="_blank" rel="noopener noreferrer">
                <Icon name="printer" size={16} /> {t('sh.print_label')}
              </a>
            ) : null}
          </span>
        </>
      }
    >
      <div className="sh-head">
        <TrackBadge s={s} />
        {s.track_detail ? <span className="muted small">{s.track_detail.replace(/_/g, ' ')}</span> : null}
      </div>
      {s.error ? (
        <p className="pm-err small">
          <Icon name="alert" size={14} /> {t('pm.last_error', { error: s.error })}
        </p>
      ) : null}
      <dl className="sh-facts">
        <dt>{t('sh.to')}</dt>
        <dd>{[a.name, a.company, a.street1, a.street2, [a.zip, a.city].filter(Boolean).join(' '), a.state, a.country].filter(Boolean).join(', ')}</dd>
        <dt>{t('sh.col_carrier')}</dt>
        <dd>{[s.carrier, s.service].filter(Boolean).join(' · ')}</dd>
        {s.customs ? (
          <>
            <dt>{t('sh.customs')}</dt>
            <dd>
              {[
                t(`sh.ct_${s.customs.contents_type}` as MessageKey),
                tn('sh.customs_lines_count', s.customs.items.length),
                moneyIn(
                  s.customs.items.reduce((n, i) => n + i.value, 0),
                  s.customs.currency,
                ),
              ].join(' · ')}
            </dd>
          </>
        ) : null}
        {s.tracking_code ? (
          <>
            <dt>{t('sh.tracking_code')}</dt>
            <dd>
              {s.public_url ? (
                <a href={s.public_url} target="_blank" rel="noopener noreferrer">
                  {s.tracking_code} <Icon name="external" size={12} />
                </a>
              ) : (
                <span className="sh-code">{s.tracking_code}</span>
              )}
            </dd>
          </>
        ) : null}
        <dt>{t('sh.col_cost')}</dt>
        <dd>{s.amount != null ? moneyIn(s.amount, s.currency) : '—'}</dd>
        <dt>{t('sh.bought')}</dt>
        <dd>{dateTime(s.bought_at ?? '')}</dd>
        {s.est_delivery && !s.delivered_at ? (
          <>
            <dt>{t('sh.expected_label')}</dt>
            <dd>{dateTime(s.est_delivery)}</dd>
          </>
        ) : null}
        {s.delivered_at ? (
          <>
            <dt>{t('sh.delivered_at')}</dt>
            <dd>{dateTime(s.delivered_at)}</dd>
          </>
        ) : null}
      </dl>
      <h3 className="rep-sub">{t('sh.journey')}</h3>
      {s.events.length ? (
        <ol className="sh-events">
          {s.events.map((e, i) => (
            <li key={i} className={i === 0 ? 'now' : ''}>
              <span className="sh-dot" aria-hidden="true" />
              <span>
                <strong>{e.message || t(`sh.t_${e.status}` as MessageKey)}</strong>
                <span className="muted small">{e.at ? dateTime(e.at) : ''}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted small">{t('sh.no_events')}</p>
      )}
      {s.tracked_at ? <p className="muted small">{t('sh.checked', { date: dateTime(s.tracked_at) })}</p> : null}
      {cancel ? (
        <Confirm
          title={t('sh.cancel_title')}
          body={t('sh.cancel_body')}
          confirm={t('sh.cancel_label')}
          danger
          busy={busy}
          onClose={() => setCancel(false)}
          onConfirm={() =>
            void run(async () => {
              await action('shipping.cancel_label', { id });
              setCancel(false);
              await reload();
            }, t('sh.cancelled_msg'))
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- buying a label --------------------------------------------------------------------------

/**
 * Weigh the parcel, see each carrier's price, buy one. `purpose` ties the label to a record (a
 * web shop order); `to` fills in what is known of the address.
 */
export function CourierDialog({
  label,
  purpose,
  to,
  items,
  onClose,
  onBought,
}: {
  label: string;
  purpose?: { module: string; record: string };
  to?: Partial<ShipAddress>;
  /** What is in the parcel, to start the customs form with. `total` in cents. */
  items?: OrderItem[];
  onClose: () => void;
  onBought: (s: ShipmentView) => void;
}) {
  const { fail } = useUi();
  const [d, setD] = useState<Defaults | null>(null);
  useEffect(() => {
    // The business's units and usual parcel, from the settings.
    action<ForRecord | null>('shipping.for_record', { module: purpose?.module ?? 'shipping', record: purpose?.record ?? '00000000-0000-4000-8000-000000000000' }).then(
      (r) => setD(r?.defaults ?? null),
      fail,
    );
  }, [purpose?.module, purpose?.record, fail]);
  if (!d) {
    return (
      <Dialog title={t('sh.buy_title')} onClose={onClose} size="md">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  return <CourierForm d={d} label={label} purpose={purpose} to={to} items={items} onClose={onClose} onBought={onBought} />;
}

function CourierForm({
  d,
  label,
  purpose,
  to,
  items,
  onClose,
  onBought,
}: {
  d: Defaults;
  label: string;
  purpose?: { module: string; record: string };
  to?: Partial<ShipAddress>;
  items?: OrderItem[];
  onClose: () => void;
  onBought: (s: ShipmentView) => void;
}) {
  const { fail, toast } = useUi();
  const { me } = useSession();
  const units = d.units;
  const [what, setWhat] = useState(label);
  const [addr, setAddr] = useState({
    name: to?.name ?? '',
    street1: to?.street1 ?? '',
    street2: to?.street2 ?? '',
    city: to?.city ?? '',
    state: to?.state ?? '',
    zip: to?.zip ?? '',
    country: (to?.country ?? d.country ?? '').toUpperCase(),
    phone: to?.phone ?? '',
    email: to?.email ?? '',
  });
  const [p, setP] = useState({ weight: showWeight(d.weight_g, units), length: showSize(d.length_mm, units), width: showSize(d.width_mm, units), height: showSize(d.height_mm, units) });
  const [quote, setQuote] = useState<ShipmentView | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bought, setBought] = useState<ShipmentView | null>(null);
  const grams = toGrams(p.weight, units);
  const [form, setForm] = useState<CustomsForm>(() => startCustoms(items ?? [], grams ?? d.weight_g, units, d.country ?? '', me?.name ?? ''));
  const sizes = [toMm(p.length, units), toMm(p.width, units), toMm(p.height, units)];
  const someSize = [p.length, p.width, p.height].some((x) => x.trim());
  const sizeOk = !someSize || sizes.every((x) => x != null);
  const addrOk = what.trim() && addr.name.trim() && addr.street1.trim() && addr.city.trim() && addr.zip.trim() && /^[A-Za-z]{2}$/.test(addr.country.trim());
  const abroad = /^[A-Za-z]{2}$/.test(addr.country.trim()) && !!d.country && addr.country.trim().toUpperCase() !== d.country.toUpperCase();
  const customs = abroad ? readCustoms(form, units) : null;
  const customsOk = !abroad || (customs !== null && customs.weight <= (grams ?? 0));
  const edit = () => setQuote(null);
  const ask = async () => {
    setBusy(true);
    try {
      const q = await action<ShipmentView>('shipping.quote', {
        ...(purpose ? { purpose } : {}),
        label: what,
        to: { ...addr, country: addr.country.trim().toUpperCase(), street2: addr.street2 || null, state: addr.state || null, phone: addr.phone || null, email: addr.email || null },
        parcel: { weight_g: grams, ...(someSize ? { length_mm: sizes[0], width_mm: sizes[1], height_mm: sizes[2] } : {}) },
        ...(customs ? { customs: customs.payload } : {}),
      });
      setQuote(q);
      setChosen(q.rates[0]?.ref ?? null);
    } catch (err) {
      fail(err);
    }
    setBusy(false);
  };
  const buy = async () => {
    if (!quote || !chosen) return;
    setBusy(true);
    try {
      const b = await action<ShipmentView>('shipping.buy', { id: quote.id, rate: chosen });
      setBought(b);
      toast(t('sh.bought_msg', { code: b.tracking_code ?? '' }));
      onBought(b);
    } catch (err) {
      fail(err);
    }
    setBusy(false);
  };
  const rate = quote?.rates.find((r) => r.ref === chosen) ?? null;
  const unitW = units === 'imperial' ? 'lb' : 'kg';
  const unitL = units === 'imperial' ? 'in' : 'cm';

  if (bought) {
    return (
      <Dialog
        title={t('sh.bought_title')}
        onClose={onClose}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
            {bought.label_url ? (
              <a className="btn btn-primary btn-md" href={bought.label_url} target="_blank" rel="noopener noreferrer" data-autofocus>
                <Icon name="printer" size={16} /> {t('sh.print_label')}
              </a>
            ) : null}
          </>
        }
      >
        <div className="sh-done">
          <span className="sh-done-icon" aria-hidden="true">
            <Icon name="checkCircle" size={36} />
          </span>
          <strong>{[bought.carrier, bought.service].filter(Boolean).join(' · ')}</strong>
          {bought.tracking_code ? <p className="sh-msg sh-code">{bought.tracking_code}</p> : null}
          <p className="sh-msg muted">{bought.label_url ? t('sh.bought_body') : t('sh.bought_body_test')}</p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={t('sh.buy_title')}
      onClose={onClose}
      size="lg"
      footer={
        quote ? (
          <>
            <Button variant="ghost" icon="edit" onClick={edit} disabled={busy}>
              {t('sh.change')}
            </Button>
            <Button variant="primary" icon="truck" busy={busy} disabled={!rate} onClick={() => void buy()}>
              {rate ? t('sh.buy_for', { amount: moneyIn(rate.amount, rate.currency) }) : t('sh.buy')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="search" busy={busy} disabled={!addrOk || !grams || !sizeOk || !customsOk} onClick={() => void ask()}>
              {t('sh.get_prices')}
            </Button>
          </>
        )
      }
    >
      {quote ? (
        <div className="sh-rates">
          <p className="muted small">{t('sh.rates_for', { name: quote.to_address.name, city: quote.to_address.city, weight: weightText(quote.parcel.weight_g, units) })}</p>
          {!quote.rates.length ? (
            <EmptyState icon="truck" title={t('sh.no_rates')} body={quote.messages?.length ? quote.messages.join(' ') : t('sh.no_rates_body')} />
          ) : (
            <div className="sh-rate-list" role="radiogroup" aria-label={t('sh.prices')}>
              {quote.rates.map((r) => (
                <label key={r.ref} className={`sh-rate${chosen === r.ref ? ' on' : ''}`}>
                  <input type="radio" name="rate" checked={chosen === r.ref} onChange={() => setChosen(r.ref)} />
                  <span className="sh-rate-who">
                    <strong>{r.carrier}</strong>
                    <span className="muted small">{r.service.replace(/([a-z])([A-Z])/g, '$1 $2')}</span>
                  </span>
                  <span className="muted small">{r.days != null ? tn('sh.days', r.days) : ''}</span>
                  <strong className="sh-rate-amount">{moneyIn(r.amount, r.currency)}</strong>
                </label>
              ))}
            </div>
          )}
          {quote.rates.length && quote.messages?.length ? <p className="muted small">{quote.messages.join(' · ')}</p> : null}
          <p className="muted small">{t('sh.prices_note')}</p>
        </div>
      ) : (
        <>
          <Field label={t('sh.what')} hint={t('sh.what_hint')} required>
            <input className="input" value={what} maxLength={120} onChange={(e) => setWhat(e.target.value)} />
          </Field>
          <fieldset className="sh-place">
            <legend className="small muted">{t('sh.to')}</legend>
            <div className="pm-two">
              <Field label={t('sh.name')} required>
                <input className="input" value={addr.name} maxLength={80} autoComplete="name" onChange={(e) => setAddr({ ...addr, name: e.target.value })} />
              </Field>
              <Field label={t('sh.phone')}>
                <input className="input" type="tel" value={addr.phone} maxLength={40} autoComplete="tel" onChange={(e) => setAddr({ ...addr, phone: e.target.value })} />
              </Field>
            </div>
            <Field label={t('sh.line1')} required>
              <input className="input" value={addr.street1} maxLength={120} autoComplete="address-line1" onChange={(e) => setAddr({ ...addr, street1: e.target.value })} />
            </Field>
            <Field label={t('sh.line2')}>
              <input className="input" value={addr.street2} maxLength={120} autoComplete="address-line2" onChange={(e) => setAddr({ ...addr, street2: e.target.value })} />
            </Field>
            <div className="pm-two">
              <Field label={t('sh.city')} required>
                <input className="input" value={addr.city} maxLength={80} autoComplete="address-level2" onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
              </Field>
              <Field label={t('sh.postal_code')} required>
                <input className="input" value={addr.zip} maxLength={20} autoComplete="postal-code" onChange={(e) => setAddr({ ...addr, zip: e.target.value })} />
              </Field>
              <Field label={t('sh.country')} hint={t('sh.country_hint')} required>
                <input
                  className="input"
                  value={addr.country}
                  maxLength={2}
                  autoComplete="country"
                  onChange={(e) => setAddr({ ...addr, country: e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase() })}
                />
              </Field>
              <Field label={t('sh.state')} hint={t('sh.state_hint')}>
                <input className="input" value={addr.state} maxLength={40} autoComplete="address-level1" onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
              </Field>
            </div>
          </fieldset>
          <fieldset className="sh-place">
            <legend className="small muted">{t('sh.parcel')}</legend>
            <div className="sh-dims">
              <Field label={t('sh.weight', { unit: unitW })} required>
                <input className="input" inputMode="decimal" value={p.weight} onChange={(e) => setP({ ...p, weight: e.target.value })} />
              </Field>
              <Field label={t('sh.length', { unit: unitL })}>
                <input className="input" inputMode="decimal" value={p.length} onChange={(e) => setP({ ...p, length: e.target.value })} />
              </Field>
              <Field label={t('sh.width', { unit: unitL })}>
                <input className="input" inputMode="decimal" value={p.width} onChange={(e) => setP({ ...p, width: e.target.value })} />
              </Field>
              <Field label={t('sh.height', { unit: unitL })}>
                <input className="input" inputMode="decimal" value={p.height} onChange={(e) => setP({ ...p, height: e.target.value })} />
              </Field>
            </div>
            {!sizeOk ? <p className="pm-err small">{t('sh.all_sizes')}</p> : <p className="muted small">{t('sh.sizes_hint')}</p>}
          </fieldset>
          {abroad ? <CustomsPart form={form} setForm={setForm} units={units} currency={d.currency} parcelGrams={grams} /> : null}
        </>
      )}
    </Dialog>
  );
}

// ---- the customs form ------------------------------------------------------------------------

/** EasyPost's contents types (docs.easypost.com/docs/customs-infos), without dangerous goods. */
const CONTENTS = ['merchandise', 'gift', 'documents', 'sample', 'returned_goods', 'humanitarian_donation', 'other'] as const;
type ContentsType = (typeof CONTENTS)[number];
const RESTRICTIONS = ['none', 'quarantine', 'sanitary_phytosanitary_inspection', 'other'] as const;
type Restriction = (typeof RESTRICTIONS)[number];
/** The usual exemption for exports under 2,500 USD, as EasyPost's customs guide gives it. */
const NOEEI = 'NOEEI 30.37(a)';

interface OrderItem {
  name: string;
  qty: number;
  total: number;
}
interface CustomsLine {
  key: number;
  description: string;
  quantity: string;
  value: string;
  weight: string;
  hs: string;
  origin: string;
}
interface CustomsForm {
  contents_type: ContentsType;
  contents_explanation: string;
  restriction_type: Restriction;
  restriction_comments: string;
  non_delivery: 'return' | 'abandon';
  eel_pfc: string;
  signer: string;
  certify: boolean;
  lines: CustomsLine[];
  more: boolean;
}

let lineKey = 0;
const emptyLine = (origin: string): CustomsLine => ({ key: ++lineKey, description: '', quantity: '1', value: '', weight: '', hs: '', origin });

/** Starts the form from the order: each line, its price, and the parcel's weight shared by quantity. */
function startCustoms(items: OrderItem[], grams: number, units: Defaults['units'], origin: string, signer: string): CustomsForm {
  const qty = items.reduce((n, i) => n + i.qty, 0);
  const lines = items.slice(0, 100).map((i) => ({
    key: ++lineKey,
    description: i.name,
    quantity: String(i.qty),
    value: (i.total / 100).toFixed(2),
    // An estimate to start from, a little under the whole parcel (the box weighs something too).
    weight: qty ? showWeight(Math.max(1, Math.floor(((grams * 0.9) / qty) * i.qty)), units) : '',
    hs: '',
    origin,
  }));
  return {
    contents_type: 'merchandise',
    contents_explanation: '',
    restriction_type: 'none',
    restriction_comments: '',
    non_delivery: 'return',
    eel_pfc: NOEEI,
    signer,
    certify: false,
    lines: lines.length ? lines : [emptyLine(origin)],
    more: false,
  };
}

const lineOk = (l: CustomsLine, units: Defaults['units']) =>
  l.description.trim().length >= 3 &&
  /^\d+$/.test(l.quantity.trim()) &&
  Number(l.quantity) >= 1 &&
  (parseMoney(l.value) ?? 0) > 0 &&
  (toGrams(l.weight, units) ?? 0) > 0 &&
  /^[A-Za-z]{2}$/.test(l.origin.trim()) &&
  (!l.hs.trim() || /^[0-9.]{4,14}$/.test(l.hs.trim()));

/** The form as the server takes it, or null while something is missing. */
function readCustoms(f: CustomsForm, units: Defaults['units']) {
  if (!f.lines.length || !f.lines.every((l) => lineOk(l, units))) return null;
  if (f.contents_type === 'other' && !f.contents_explanation.trim()) return null;
  if (f.restriction_type !== 'none' && !f.restriction_comments.trim()) return null;
  if (!f.eel_pfc.trim() || f.signer.trim().length < 2 || !f.certify) return null;
  const items = f.lines.map((l) => ({
    description: l.description.trim(),
    quantity: Number(l.quantity),
    value: parseMoney(l.value)!,
    weight_g: toGrams(l.weight, units)!,
    hs_tariff_number: l.hs.trim() || null,
    origin_country: l.origin.trim().toUpperCase(),
  }));
  return {
    weight: items.reduce((n, i) => n + i.weight_g, 0),
    payload: {
      contents_type: f.contents_type,
      contents_explanation: f.contents_explanation.trim() || null,
      restriction_type: f.restriction_type,
      restriction_comments: f.restriction_type !== 'none' ? f.restriction_comments.trim() : null,
      non_delivery: f.non_delivery,
      eel_pfc: f.eel_pfc.trim(),
      signer: f.signer.trim(),
      certify: true,
      items,
    },
  };
}

function CustomsPart({
  form: f,
  setForm,
  units,
  currency,
  parcelGrams,
}: {
  form: CustomsForm;
  setForm: (f: CustomsForm) => void;
  units: Defaults['units'];
  currency: string;
  parcelGrams: number | null;
}) {
  const unitW = units === 'imperial' ? 'lb' : 'kg';
  const set = (patch: Partial<CustomsForm>) => setForm({ ...f, ...patch });
  const setLine = (key: number, patch: Partial<CustomsLine>) => set({ lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) });
  const total = f.lines.reduce((n, l) => n + (parseMoney(l.value) ?? 0), 0);
  const weight = f.lines.reduce((n, l) => n + (toGrams(l.weight, units) ?? 0), 0);
  const heavy = parcelGrams != null && weight > parcelGrams;
  const origin = f.lines[0]?.origin ?? '';
  return (
    <fieldset className="sh-place sh-customs">
      <legend className="small muted">{t('sh.customs')}</legend>
      <p className="callout callout-info sh-customs-lead">
        <Icon name="globe" size={16} />
        <span>{t('sh.customs_lead')}</span>
      </p>
      <div className="pm-two">
        <Field label={t('sh.contents')} required>
          <select className="input" value={f.contents_type} onChange={(e) => set({ contents_type: e.target.value as ContentsType })}>
            {CONTENTS.map((c) => (
              <option key={c} value={c}>
                {t(`sh.ct_${c}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
        {f.contents_type === 'other' ? (
          <Field label={t('sh.contents_explain')} required>
            <input className="input" value={f.contents_explanation} maxLength={255} onChange={(e) => set({ contents_explanation: e.target.value })} />
          </Field>
        ) : null}
      </div>
      <div className="sh-lines" role="group" aria-label={t('sh.customs_lines')}>
        {f.lines.map((l, i) => {
          const n = i + 1;
          const bad = (l.description || l.value || l.weight) && !lineOk(l, units);
          return (
            <div key={l.key} className={`sh-line${bad ? ' bad' : ''}`}>
              <Field label={t('sh.l_description')} required>
                <input className="input" value={l.description} maxLength={120} aria-label={t('sh.l_description_n', { n })} onChange={(e) => setLine(l.key, { description: e.target.value })} />
              </Field>
              <Field label={t('sh.l_quantity')} required>
                <input
                  className="input"
                  inputMode="numeric"
                  value={l.quantity}
                  aria-label={t('sh.l_quantity_n', { n })}
                  onChange={(e) => setLine(l.key, { quantity: e.target.value.replace(/\D/g, '') })}
                />
              </Field>
              <Field label={t('sh.l_value', { currency })} required>
                <input className="input" inputMode="decimal" value={l.value} aria-label={t('sh.l_value_n', { n })} onChange={(e) => setLine(l.key, { value: e.target.value })} />
              </Field>
              <Field label={t('sh.l_weight', { unit: unitW })} required>
                <input className="input" inputMode="decimal" value={l.weight} aria-label={t('sh.l_weight_n', { n })} onChange={(e) => setLine(l.key, { weight: e.target.value })} />
              </Field>
              <Field label={t('sh.l_hs')}>
                <input
                  className="input"
                  inputMode="decimal"
                  value={l.hs}
                  maxLength={14}
                  placeholder="6109.10"
                  aria-label={t('sh.l_hs_n', { n })}
                  onChange={(e) => setLine(l.key, { hs: e.target.value.replace(/[^\d.]/g, '') })}
                />
              </Field>
              <Field label={t('sh.l_origin')} required>
                <input
                  className="input"
                  value={l.origin}
                  maxLength={2}
                  aria-label={t('sh.l_origin_n', { n })}
                  onChange={(e) => setLine(l.key, { origin: e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase() })}
                />
              </Field>
              {f.lines.length > 1 ? (
                <button
                  type="button"
                  className="icon-btn btn-ghost sh-line-remove"
                  aria-label={t('sh.remove_line', { n })}
                  title={t('sh.remove_line', { n })}
                  onClick={() => set({ lines: f.lines.filter((x) => x.key !== l.key) })}
                >
                  <Icon name="trash" size={18} />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="sh-lines-foot">
        {f.lines.length < 100 ? (
          <Button size="sm" icon="plus" onClick={() => set({ lines: [...f.lines, emptyLine(origin)] })}>
            {t('sh.add_line')}
          </Button>
        ) : (
          <span />
        )}
        <span className={`small${heavy ? ' pm-err' : ' muted'}`}>{heavy ? t('sh.customs_heavy') : t('sh.customs_total', { value: moneyIn(total, currency), weight: weightText(weight, units) })}</span>
      </div>
      <p className="muted small">{t('sh.l_hint')}</p>
      <button type="button" className="sh-more" aria-expanded={f.more} onClick={() => set({ more: !f.more })}>
        <Icon name="chevronRight" size={14} /> {t('sh.customs_more')}
      </button>
      {f.more ? (
        <div className="pm-two">
          <Field label={t('sh.restriction')}>
            <select className="input" value={f.restriction_type} onChange={(e) => set({ restriction_type: e.target.value as Restriction })}>
              {RESTRICTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`sh.rs_${r}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
          {f.restriction_type !== 'none' ? (
            <Field label={t('sh.restriction_comments')} required>
              <input className="input" value={f.restriction_comments} maxLength={255} onChange={(e) => set({ restriction_comments: e.target.value })} />
            </Field>
          ) : null}
          <Field label={t('sh.non_delivery')}>
            <select className="input" value={f.non_delivery} onChange={(e) => set({ non_delivery: e.target.value as 'return' | 'abandon' })}>
              <option value="return">{t('sh.nd_return')}</option>
              <option value="abandon">{t('sh.nd_abandon')}</option>
            </select>
          </Field>
          <Field label={t('sh.eel_pfc')} hint={t('sh.eel_pfc_hint')} required>
            <input className="input" value={f.eel_pfc} maxLength={60} onChange={(e) => set({ eel_pfc: e.target.value })} />
          </Field>
        </div>
      ) : null}
      <Field label={t('sh.signer')} hint={t('sh.signer_hint')} required>
        <input className="input" value={f.signer} maxLength={80} autoComplete="name" onChange={(e) => set({ signer: e.target.value })} />
      </Field>
      <label className="check-row">
        <input type="checkbox" checked={f.certify} onChange={(e) => set({ certify: e.target.checked })} />
        <span>
          <span className="strong">{t('sh.certify')}</span>
          <span className="muted">{t('sh.certify_hint')}</span>
        </span>
      </label>
    </fieldset>
  );
}

// ---- settings ----------------------------------------------------------------------------------

interface CourierAccount {
  id: string;
  name: string;
  courier: 'easypost' | 'simulator';
  active: boolean;
  is_default: boolean;
  live: boolean;
  checked_at: string | null;
  last_error: string | null;
  has_secret: boolean;
  secret_readable: boolean;
  labels: number;
}
interface SettingsData {
  can_seal: boolean;
  sender_ready: boolean;
  settings: {
    from_name: string | null;
    from_company: string | null;
    from_street1: string | null;
    from_street2: string | null;
    from_city: string | null;
    from_state: string | null;
    from_zip: string | null;
    from_country: string | null;
    from_phone: string | null;
    from_email: string | null;
    units: 'metric' | 'imperial';
    weight_g: number;
    length_mm: number | null;
    width_mm: number | null;
    height_mm: number | null;
  };
  accounts: CourierAccount[];
}

function SettingsTab({ onChange }: { onChange: () => Promise<unknown> }) {
  const { data: s, reload } = useAction<SettingsData>('shipping.settings');
  const { busy, run } = useSave();
  const { toast, fail } = useUi();
  const [edit, setEdit] = useState<CourierAccount | 'easypost' | 'simulator' | null>(null);
  const [remove, setRemove] = useState<CourierAccount | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const changed = async () => {
    await reload();
    await onChange();
  };
  if (!s) return <Skeleton h={320} />;
  const check = async (a: CourierAccount) => {
    setChecking(a.id);
    try {
      const r = await action<{ ok: boolean; error?: string }>('shipping.check_account', { id: a.id });
      if (r.ok) toast(t('sh.check_ok', { name: a.name }));
      else toast(r.error ?? t('error.generic'), 'danger');
      await changed();
    } catch (err) {
      fail(err);
    }
    setChecking(null);
  };
  return (
    <div className="pm-settings">
      {!s.can_seal ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} /> {t('pm.no_seal')}
        </p>
      ) : null}
      <section className="panel">
        <div className="pm-panel-head">
          <div>
            <h2 className="pm-h2">{t('sh.services')}</h2>
            <p className="muted small">{t('sh.services_lead')}</p>
          </div>
          <span className="pm-actions">
            <Button icon="plus" variant="primary" onClick={() => setEdit('easypost')}>
              {t('sh.add_easypost')}
            </Button>
            <Button icon="plus" onClick={() => setEdit('simulator')}>
              {t('sh.add_simulator')}
            </Button>
          </span>
        </div>
        {!s.accounts.length ? (
          <EmptyState icon="truck" title={t('sh.no_accounts')} body={t('sh.no_accounts_body')} />
        ) : (
          <ul className="pm-accounts">
            {s.accounts.map((a) => (
              <li key={a.id} className={`pm-acc${a.active ? '' : ' off'}`}>
                <div className="pm-acc-head">
                  <span className="pm-acc-icon" aria-hidden="true">
                    <Icon name={a.courier === 'easypost' ? 'truck' : 'cpu'} size={18} />
                  </span>
                  <span className="pm-who">
                    <strong>
                      {a.name}
                      {a.is_default ? <span className="pm-default">{t('pm.default')}</span> : null}
                    </strong>
                    <span className="muted small">
                      {[t(`sh.cv_${a.courier}` as MessageKey), a.labels ? tn('sh.labels', a.labels) : null, a.checked_at ? t('pm.checked_at', { date: dateTime(a.checked_at) }) : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {!a.active ? (
                    <StatusBadge tone="neutral" icon="pause" label={t('pm.off')} />
                  ) : a.courier === 'simulator' || !a.live ? (
                    <StatusBadge tone="warn" icon="alert" label={t('pm.test')} />
                  ) : (
                    <StatusBadge tone="ok" icon="check" label={t('pm.live')} />
                  )}
                </div>
                {a.last_error || !a.secret_readable ? (
                  <ul className="pm-problems">
                    <li className="pm-err">
                      <Icon name="alert" size={14} /> {a.last_error ? t('pm.last_error', { error: a.last_error }) : t('sh.key_unreadable')}
                    </li>
                  </ul>
                ) : null}
                <div className="pm-actions">
                  <Button size="sm" icon="edit" onClick={() => setEdit(a)}>
                    {t('pm.edit')}
                  </Button>
                  {a.courier !== 'simulator' ? (
                    <Button size="sm" icon="repeat" busy={checking === a.id} disabled={!a.has_secret} onClick={() => void check(a)}>
                      {t('pm.check_connection')}
                    </Button>
                  ) : null}
                  {a.labels === 0 ? (
                    <Button size="sm" variant="ghost" icon="trash" onClick={() => setRemove(a)}>
                      {t('pm.remove')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <SenderPanel s={s} onSaved={changed} />
      {edit ? (
        <AccountDialog account={typeof edit === 'string' ? null : edit} courier={typeof edit === 'string' ? edit : edit.courier} canSeal={s.can_seal} onClose={() => setEdit(null)} onSaved={changed} />
      ) : null}
      {remove ? (
        <Confirm
          title={t('pm.remove_title', { name: remove.name })}
          body={t('sh.remove_body')}
          confirm={t('pm.remove')}
          danger
          busy={busy}
          onClose={() => setRemove(null)}
          onConfirm={() =>
            void run(async () => {
              await action('shipping.delete_account', { id: remove.id });
              setRemove(null);
              await changed();
            }, t('pm.removed'))
          }
        />
      ) : null}
    </div>
  );
}

function SenderPanel({ s, onSaved }: { s: SettingsData; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const v = s.settings;
  const [units, setUnits] = useState(v.units);
  const [f, setF] = useState({
    from_name: v.from_name ?? '',
    from_company: v.from_company ?? '',
    from_street1: v.from_street1 ?? '',
    from_street2: v.from_street2 ?? '',
    from_city: v.from_city ?? '',
    from_state: v.from_state ?? '',
    from_zip: v.from_zip ?? '',
    from_country: v.from_country ?? '',
    from_phone: v.from_phone ?? '',
    from_email: v.from_email ?? '',
  });
  const [p, setP] = useState({ weight: showWeight(v.weight_g, v.units), length: showSize(v.length_mm, v.units), width: showSize(v.width_mm, v.units), height: showSize(v.height_mm, v.units) });
  const switchUnits = (u: 'metric' | 'imperial') => {
    // The same parcel, shown in the other units.
    const g = toGrams(p.weight, units);
    const mm = [toMm(p.length, units), toMm(p.width, units), toMm(p.height, units)];
    setP({ weight: g ? showWeight(g, u) : '', length: showSize(mm[0] ?? null, u), width: showSize(mm[1] ?? null, u), height: showSize(mm[2] ?? null, u) });
    setUnits(u);
  };
  const grams = toGrams(p.weight, units);
  const sizes = [toMm(p.length, units), toMm(p.width, units), toMm(p.height, units)];
  const ok = f.from_name.trim() && f.from_street1.trim() && f.from_city.trim() && f.from_zip.trim() && /^[A-Za-z]{2}$/.test(f.from_country.trim()) && grams;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: k === 'from_country' ? e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase() : e.target.value });
  return (
    <section className="panel">
      <h2 className="pm-h2">{t('sh.sender')}</h2>
      <p className="muted small">{t('sh.sender_lead')}</p>
      <div className="pm-two">
        <Field label={t('sh.sender_name')} required>
          <input className="input" value={f.from_name} maxLength={80} onChange={set('from_name')} />
        </Field>
        <Field label={t('sh.company')}>
          <input className="input" value={f.from_company} maxLength={80} onChange={set('from_company')} />
        </Field>
        <Field label={t('sh.line1')} required>
          <input className="input" value={f.from_street1} maxLength={120} onChange={set('from_street1')} />
        </Field>
        <Field label={t('sh.line2')}>
          <input className="input" value={f.from_street2} maxLength={120} onChange={set('from_street2')} />
        </Field>
        <Field label={t('sh.city')} required>
          <input className="input" value={f.from_city} maxLength={80} onChange={set('from_city')} />
        </Field>
        <Field label={t('sh.postal_code')} required>
          <input className="input" value={f.from_zip} maxLength={20} onChange={set('from_zip')} />
        </Field>
        <Field label={t('sh.country')} hint={t('sh.country_hint')} required>
          <input className="input" value={f.from_country} maxLength={2} onChange={set('from_country')} />
        </Field>
        <Field label={t('sh.state')} hint={t('sh.state_hint')}>
          <input className="input" value={f.from_state} maxLength={40} onChange={set('from_state')} />
        </Field>
        <Field label={t('sh.phone')} hint={t('sh.phone_hint')}>
          <input className="input" type="tel" value={f.from_phone} maxLength={40} onChange={set('from_phone')} />
        </Field>
        <Field label={t('sh.email')}>
          <input className="input" type="email" value={f.from_email} maxLength={120} onChange={set('from_email')} />
        </Field>
      </div>
      <h3 className="rep-sub">{t('sh.usual_parcel')}</h3>
      <div className="segmented sh-units" role="tablist" aria-label={t('sh.units')}>
        {(['metric', 'imperial'] as const).map((u) => (
          <button key={u} role="tab" aria-selected={units === u} className={units === u ? 'active' : ''} onClick={() => switchUnits(u)}>
            {t(`sh.units_${u}` as MessageKey)}
          </button>
        ))}
      </div>
      <div className="sh-dims">
        <Field label={t('sh.weight', { unit: units === 'imperial' ? 'lb' : 'kg' })} required>
          <input className="input" inputMode="decimal" value={p.weight} onChange={(e) => setP({ ...p, weight: e.target.value })} />
        </Field>
        <Field label={t('sh.length', { unit: units === 'imperial' ? 'in' : 'cm' })}>
          <input className="input" inputMode="decimal" value={p.length} onChange={(e) => setP({ ...p, length: e.target.value })} />
        </Field>
        <Field label={t('sh.width', { unit: units === 'imperial' ? 'in' : 'cm' })}>
          <input className="input" inputMode="decimal" value={p.width} onChange={(e) => setP({ ...p, width: e.target.value })} />
        </Field>
        <Field label={t('sh.height', { unit: units === 'imperial' ? 'in' : 'cm' })}>
          <input className="input" inputMode="decimal" value={p.height} onChange={(e) => setP({ ...p, height: e.target.value })} />
        </Field>
      </div>
      <div className="sh-save">
        <Button
          variant="primary"
          icon="check"
          busy={busy}
          disabled={!ok}
          onClick={() =>
            void run(async () => {
              const all = sizes.every((x) => x != null);
              await action('shipping.save_settings', {
                ...f,
                from_country: f.from_country.trim().toUpperCase(),
                units,
                weight_g: grams,
                length_mm: all ? sizes[0] : null,
                width_mm: all ? sizes[1] : null,
                height_mm: all ? sizes[2] : null,
              });
              await onSaved();
            }, t('pm.saved'))
          }
        >
          {t('pm.save')}
        </Button>
      </div>
    </section>
  );
}

function AccountDialog({
  account,
  courier,
  canSeal,
  onClose,
  onSaved,
}: {
  account: CourierAccount | null;
  courier: 'easypost' | 'simulator';
  canSeal: boolean;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: account?.name ?? (courier === 'easypost' ? 'EasyPost' : t('sh.simulator_name')),
    active: account?.active ?? true,
    is_default: account?.is_default ?? true,
    secret: '',
  });
  const ep = courier === 'easypost';
  const keyOk = !f.secret || !/\s/.test(f.secret.trim());
  return (
    <Dialog
      title={account ? t('pm.edit_account', { name: account.name }) : ep ? t('sh.add_easypost') : t('sh.add_simulator')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.name.trim() || !keyOk || (ep && !account && !f.secret.trim())}
            onClick={() =>
              void run(async () => {
                await action('shipping.save_account', {
                  ...(account ? { id: account.id } : {}),
                  name: f.name,
                  courier,
                  active: f.active,
                  is_default: f.is_default,
                  ...(f.secret.trim() ? { secret: f.secret.trim() } : {}),
                });
                onClose();
                await onSaved();
              }, t('pm.saved'))
            }
          >
            {t('pm.save')}
          </Button>
        </>
      }
    >
      {ep ? (
        <ol className="pm-steps">
          <li>{t('sh.ep_step1')}</li>
          <li>{t('sh.ep_step2')}</li>
          <li>{t('sh.ep_step3')}</li>
        </ol>
      ) : (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} /> {t('sh.simulator_lead')}
        </p>
      )}
      <Field label={t('pm.account_name')} required>
        <input className="input" value={f.name} maxLength={60} onChange={(e) => setF({ ...f, name: e.target.value })} />
      </Field>
      {ep ? (
        <>
          {!canSeal ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} /> {t('pm.no_seal')}
            </p>
          ) : null}
          <Field label={t('sh.api_key')} hint={account?.has_secret ? t('pm.secret_kept') : t('sh.api_key_hint')} error={keyOk ? null : t('sh.api_key_bad')} required={!account}>
            <input
              className="input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={f.secret}
              placeholder={account?.has_secret ? '••••••••' : ''}
              onChange={(e) => setF({ ...f, secret: e.target.value })}
            />
          </Field>
        </>
      ) : null}
      <div className="pm-switches">
        <label className="pm-switch">
          <Switch checked={f.is_default} onChange={(v) => setF({ ...f, is_default: v })} label={t('sh.use_default')} />
          <span>{t('sh.use_default')}</span>
        </label>
        <label className="pm-switch">
          <Switch checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={t('pm.active')} />
          <span>{t('pm.active')}</span>
        </label>
      </div>
    </Dialog>
  );
}
